import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';

// The banner file is uploaded to S3 by the "Banner" GitHub workflow, independently of app deployments.
// See README > "Showing a maintenance banner".
export const SYSTEM_BANNER_URL = `${import.meta.env.BASE_URL}runtime/banner.json`;

const SYSTEM_BANNER_REFETCH_INTERVAL_IN_MS = 5 * 60 * 1000;

const hexColorSchema = z.string().regex(/^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/);

export const systemBannerSchema = z.object({
  id: z.string().min(1),
  severity: z.enum(['info', 'warning', 'critical']).default('info'),
  message: z.object({
    en: z.string().min(1),
    'en-CA': z.string().min(1).optional(),
  }),
  startsAt: z.string().datetime({ offset: true }).optional(),
  endsAt: z.string().datetime({ offset: true }).optional(),
  dismissible: z.boolean().default(true),
  // Blocks guardians from scheduling, rescheduling and cancelling while the banner is active (even if dismissed)
  blockScheduling: z.boolean().default(false),
  // Guardians with a student at one of these centers aren't blocked, so QA can test with test centers during the window
  exemptCenterIds: z.array(z.number().int()).default([]),
  colors: z
    .object({
      background: hexColorSchema,
      text: hexColorSchema,
    })
    .optional(),
});

export type SystemBanner = z.infer<typeof systemBannerSchema>;

// Each banner is validated on its own so one malformed entry doesn't hide the others.
export const parseSystemBanners = (payload: unknown): SystemBanner[] => {
  const file = z.object({ banners: z.array(z.unknown()) }).safeParse(payload);
  if (!file.success) return [];

  return file.data.banners.flatMap((banner) => {
    const result = systemBannerSchema.safeParse(banner);
    return result.success ? [result.data] : [];
  });
};

export const isSystemBannerActive = (banner: SystemBanner, now: Date) => {
  if (banner.startsAt && new Date(banner.startsAt) > now) return false;
  if (banner.endsAt && new Date(banner.endsAt) <= now) return false;
  return true;
};

// Never throws: a missing file, the SPA fallback serving index.html, or invalid JSON all mean "no banner".
export const getSystemBanners = async (): Promise<SystemBanner[]> => {
  try {
    const response = await fetch(SYSTEM_BANNER_URL, { cache: 'no-store' });
    if (!response.ok) return [];
    return parseSystemBanners(await response.json());
  } catch {
    return [];
  }
};

export const useSystemBanners = () => {
  return useQuery({
    queryKey: ['system-banners'],
    queryFn: getSystemBanners,
    refetchInterval: SYSTEM_BANNER_REFETCH_INTERVAL_IN_MS,
  });
};
